import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAssistantConfig } from "../hooks/useAssistantConfig";
import { ConfigGeneralSection } from "@/features/Assistant/components/config/ConfigGeneralSection";
import { ConfigTeamsSection } from "@/features/Assistant/components/config/ConfigTeamsSection";
import type { AssistantConfig } from "../services/configService";

interface AssistantConfigPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MASKED_SECRET = "***";

// Helper para converter string "true"/"false" em boolean
const parseBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

export const AssistantConfigPanel = ({ open, onOpenChange }: AssistantConfigPanelProps) => {
  const { t } = useTranslation();
  const { config: apiConfig, loading, saving, error, success, loadConfig, saveConfig } = useAssistantConfig();

  const [config, setConfig] = useState({
    baseUrl: "",
    engine: "",
    apiBaseUrl: "",
    apiKey: "",
    agentName: "",
    mailboxEnabled: false,
    mailbox: "",
    pollInterval: "",
    horizonDays: "",
    maxWaitMinutes: "",
    graphTenantId: "",
    graphClientId: "",
    graphClientSecret: "",
    logsApiKey: "",
    logsDirectory: "",
    azureAdTenantId: "",
    azureAdClientId: "",
    azureAdClientSecret: "",
    botUserId: "",
    botName: "",
    botBaseUrl: "",
    certThumbprint: "",
  });

  useEffect(() => {
    if (open && !apiConfig) {
      loadConfig();
    }
  }, [open, apiConfig, loadConfig]);

  useEffect(() => {
    if (apiConfig) {
      setConfig({
        baseUrl: apiConfig.BaseUrl || "",
        engine: apiConfig.TranscriptEngine || "",
        apiBaseUrl: apiConfig.ProdgyBack?.BaseUrl || "",
        apiKey: apiConfig.ProdgyBack?.ApiKey || "",
        agentName: apiConfig.ProdgyBack?.AgentName || "",
        mailboxEnabled: parseBoolean(apiConfig.Calendar?.Enabled),
        mailbox: apiConfig.Calendar?.Mailbox || "",
        pollInterval: String(apiConfig.Calendar?.PollIntervalMinutes ?? ""),
        horizonDays: String(apiConfig.Calendar?.HorizonDays ?? ""),
        maxWaitMinutes: String(apiConfig.GraphCommsBot?.MaxWaitMinutes ?? ""),
        graphTenantId: apiConfig.Calendar?.Graph?.TenantId || "",
        graphClientId: apiConfig.Calendar?.Graph?.ClientId || "",
        graphClientSecret: apiConfig.Calendar?.Graph?.ClientSecret || "",
        logsApiKey: apiConfig.LogsApi?.ApiKey || "",
        logsDirectory: apiConfig.LogsApi?.LogsDirectory || "",
        azureAdTenantId: apiConfig.AzureAd?.TenantId || "",
        azureAdClientId: apiConfig.AzureAd?.ClientId || "",
        azureAdClientSecret: apiConfig.AzureAd?.ClientSecret || "",
        botUserId: apiConfig.AzureAd?.BotUserId || "",
        botName: apiConfig.GraphCommsBot?.Name || "",
        botBaseUrl: apiConfig.GraphCommsBot?.BaseUrl || "",
        certThumbprint: apiConfig.GraphCommsBot?.CertificateThumbprint || "",
      });
    }
  }, [apiConfig]);

  const handleChange = (field: string, value: string | boolean) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const updatedConfig: AssistantConfig = {
      BaseUrl: config.baseUrl,
      TranscriptEngine: config.engine,
      ProdgyBack: {
        BaseUrl: config.apiBaseUrl,
        ApiKey: (config.apiKey === MASKED_SECRET || !config.apiKey) ? undefined : config.apiKey,
        AgentName: config.agentName,
      },
      Calendar: {
        Enabled: config.mailboxEnabled,
        Mailbox: config.mailbox,
        PollIntervalMinutes: parseInt(config.pollInterval) || undefined,
        HorizonDays: parseInt(config.horizonDays) || undefined,
        Graph: {
          TenantId: config.graphTenantId,
          ClientId: config.graphClientId,
          ClientSecret: (config.graphClientSecret === MASKED_SECRET || !config.graphClientSecret) ? undefined : config.graphClientSecret,
        },
      },
      GraphCommsBot: {
        Name: config.botName,
        BaseUrl: config.botBaseUrl,
        MaxWaitMinutes: parseInt(config.maxWaitMinutes) || undefined,
        CertificateThumbprint: (config.certThumbprint === MASKED_SECRET || !config.certThumbprint) ? undefined : config.certThumbprint,
      },
      LogsApi: {
        ApiKey: (config.logsApiKey === MASKED_SECRET || !config.logsApiKey) ? undefined : config.logsApiKey,
        LogsDirectory: config.logsDirectory,
      },
      AzureAd: {
        TenantId: config.azureAdTenantId,
        ClientId: config.azureAdClientId,
        ClientSecret: (config.azureAdClientSecret === MASKED_SECRET || !config.azureAdClientSecret) ? undefined : config.azureAdClientSecret,
        BotUserId: config.botUserId,
      },
    };

    await saveConfig(updatedConfig);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle>{t("assistant.config.title")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-sm text-muted-foreground">{t("assistant.config.loading")}</div>
            </div>
          ) : (
            <>
              <ConfigGeneralSection config={config} onChange={handleChange} />
              <ConfigTeamsSection config={config} onChange={handleChange} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t px-6 py-4">
          {error && (
            <div className="mb-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-3 p-2 bg-green-500/10 border border-green-500/20 rounded text-xs text-green-600">
              {success}
            </div>
          )}
          <p className="text-xs text-muted-foreground mb-3 text-center">
            {t("assistant.config.restartWarning")}
          </p>
          <Button onClick={handleSave} className="w-full" disabled={saving || loading}>
            {saving ? t("assistant.config.saving") : t("assistant.config.save")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
